import type { PatientStatus } from "@/server/types/patient-status";
import type { HealthSystemOption } from "@/frontend/types/dashboard/health-systems";

/** Persisted progress for the retryable add-patient workflow. */
export type AddPatientWorkflowProgress = {
  patientMembershipId: string | null;
  completedSourceUrls: ReadonlySet<string>;
  magicLinkCompleted: boolean;
};

/** Immutable inputs captured for one add-patient workflow. */
export type AddPatientWorkflowInput = {
  name: string;
  email: string;
  suggested: HealthSystemOption[];
  generateLink: boolean;
};

/** Minimal API seam used by the add-patient workflow and its retry tests. */
export type AddPatientWorkflowClient = {
  createUser: (input: { name: string; email: string; role: "user" }) => Promise<{ id: string }>;
  addUserRecommendedSource: (input: { patientMembershipId: string; fhirBaseUrl: string }) => Promise<unknown>;
  generateMagicLink: (input: { patientMembershipId: string }) => Promise<unknown>;
};

/** Properties for the patient deletion confirmation dialog. */
export type DeletePatientDialogProps = {
  patientMembershipId: string;
  patientName: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** The filter chips, in display order. `countKey` picks the chip's count out of `getPatientListCounts`. */
export const SEGMENTS = [
  { id: "all", label: "All", countKey: "total" },
  { id: "connected", label: "Connected", countKey: "connected" },
  { id: "attention", label: "Needs attention", countKey: "needsAttention" },
  { id: "invited", label: "Invited", countKey: "invited" },
  { id: "notInvited", label: "Not invited", countKey: "notInvited" },
] as const;

/**
 * Shared column template for the patient table's header and rows, so the two
 * grids always align. The 3rem tail is the chevron column; px-6 on the row
 * container keeps the 24px page-gutter symmetry.
 */
export const PATIENT_TABLE_GRID =
  "grid grid-cols-[minmax(0,28fr)_minmax(0,16fr)_minmax(0,16fr)_minmax(0,16fr)_minmax(0,20fr)_3rem] items-center";

/** Row height in px; matches the row's `h-14` so the virtualizer estimate is exact. */
export const ROW_HEIGHT = 56;

/** Deterministic avatar tint per patient, so a row keeps the same colour across pages. */
export const AVATAR_TINTS = [
  "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
];

/** Rows fetched per scroll chunk. Only fetched chunks are ever held in memory. */
export const PATIENTS_PAGE_SIZE = 50;

/** How often the open patient table re-reads, so new connections and syncs show without a reload. */
export const PATIENTS_REFRESH_INTERVAL_MS = 10_000;

/** Sortable columns of the patient table, named after `listPatients.sortBy`. */
export type PatientSortBy = "name" | "records" | "lastActivity";
export type PatientSortDir = "asc" | "desc";

export type PatientTableHeaderProps = {
  sortBy: PatientSortBy | undefined;
  sortDir: PatientSortDir | undefined;
  /** Writes the next sort state; `undefined` clears back to the lastActivity-desc default. */
  onSortChange: (sortBy: PatientSortBy | undefined, sortDir: PatientSortDir | undefined) => void;
};

/** One of the patient table's filter chips, derived from `SEGMENTS` so the chips stay the single source of truth. */
export type SegmentId = (typeof SEGMENTS)[number]["id"];

/** Filters and sort for the patient table. Part of the infinite query key, so pages reset when any of them change. */
export type PatientTableFilters = {
  q?: string;
  segment?: SegmentId;
  statuses?: PatientStatus[];
  sourceIds?: string[];
  sortBy?: PatientSortBy;
  sortDir?: PatientSortDir;
};
